/**
 * Shared standings math (through a given week index) for SeasonStandings, playoff bracket, etc.
 */

import { Coach as CoachModel } from '../model/Coach';
import { Division as DivisionModel, Week as WeekModel } from '../model/Season';
import { createComponentLogger } from './logger';

const log = createComponentLogger('seasonStandingsCore');

export interface TeamStats {
    teamId: string;
    teamName: string;
    race: string;
    coachName: string;
    divisionName: string;
    gamesPlayed: number;
    wins: number;
    ties: number;
    losses: number;
    points: number;
    divisionPoints: number;
    touchdownsFor: number;
    touchdownsAgainst: number;
    diff: number;
    casualtiesFor: number;
    casualtiesAgainst: number;
    kills?: number;
    blocks?: number;
    avBr?: number;
    ko?: number;
    surfs?: number;
    fouls?: number;
    passes?: number;
    spp?: number;
    killsAgainst?: number;
    blocksAgainst?: number;
    avBrAgainst?: number;
    koAgainst?: number;
    surfsAgainst?: number;
    foulsAgainst?: number;
    passesAgainst?: number;
    sppAgainst?: number;
}

export function isRegularSeasonWeekName(weekName: string): boolean {
    if (!weekName) {
        log.error('Week name is undefined or null');
        return false;
    }
    const lower = weekName.toLowerCase();
    return !lower.includes('preseason') && !lower.includes('postseason');
}

export function isWeekComplete(week: WeekModel | null | undefined): boolean {
    if (!week || !Array.isArray(week.matches) || week.matches.length === 0) {
        return false;
    }
    return week.matches.every(match => match && match.results !== undefined && match.results !== null);
}

/** True when every regular-season week has full results. */
export function isRegularSeasonComplete(weeks: WeekModel[] | null | undefined): boolean {
    if (!Array.isArray(weeks) || weeks.length === 0) return false;
    return weeks.every((week, _i) => {
        if (!week?.weekName || !isRegularSeasonWeekName(week.weekName)) return true;
        return isWeekComplete(week);
    });
}

/** Index of the last week in the schedule that counts as regular season (by name), or -1. */
export function lastRegularSeasonWeekIndex(weeks: WeekModel[] | null | undefined): number {
    if (!Array.isArray(weeks)) return -1;
    let last = -1;
    weeks.forEach((week, index) => {
        if (week?.weekName && isRegularSeasonWeekName(week.weekName)) {
            last = index;
        }
    });
    return last;
}

/** League-wide tie-breakers (no division points). Used when comparing teams from different divisions (e.g. playoff pool ordering). */
export function compareByTieBreakerLeagueWide(a: TeamStats, b: TeamStats): number {
    if (b.points !== a.points) return b.points - a.points;
    if (b.wins !== a.wins) return b.wins - a.wins;
    if (b.diff !== a.diff) return b.diff - a.diff;
    if (b.touchdownsFor !== a.touchdownsFor) return b.touchdownsFor - a.touchdownsFor;
    return b.casualtiesFor - a.casualtiesFor;
}

/** Within-division tie-breakers (includes division points). */
export function compareByTieBreakerWithinDivision(a: TeamStats, b: TeamStats): number {
    if (b.points !== a.points) return b.points - a.points;
    if (b.divisionPoints !== a.divisionPoints) return b.divisionPoints - a.divisionPoints;
    if (b.wins !== a.wins) return b.wins - a.wins;
    if (b.diff !== a.diff) return b.diff - a.diff;
    if (b.touchdownsFor !== a.touchdownsFor) return b.touchdownsFor - a.touchdownsFor;
    return b.casualtiesFor - a.casualtiesFor;
}

/**
 * Ordering division winners + wildcard candidates together: same division → within-division tiebreakers
 * (including division points); different divisions → league-wide tiebreakers only.
 */
export function compareByTieBreakerPlayoffPool(a: TeamStats, b: TeamStats): number {
    if (a.divisionName === b.divisionName) {
        return compareByTieBreakerWithinDivision(a, b);
    }
    return compareByTieBreakerLeagueWide(a, b);
}

export interface SeasonStandingsInput {
    divisions: DivisionModel[];
    weeks: WeekModel[];
}

export function calculateTeamStats(
    seasonData: SeasonStandingsInput,
    leagueCoaches: CoachModel[],
    teamId: string,
    upToWeekIndex: number
): TeamStats | null {
    try {
        if (!teamId) return null;

        let gamesPlayed = 0;
        let wins = 0;
        let divisionWins = 0;
        let ties = 0;
        let divisionTies = 0;
        let losses = 0;
        let touchdownsFor = 0;
        let touchdownsAgainst = 0;
        let casualtiesFor = 0;
        let casualtiesAgainst = 0;
        let kills = 0;
        let blocks = 0;
        let avBr = 0;
        let ko = 0;
        let surfs = 0;
        let fouls = 0;
        let passes = 0;
        let spp = 0;
        let killsAgainst = 0;
        let blocksAgainst = 0;
        let avBrAgainst = 0;
        let koAgainst = 0;
        let surfsAgainst = 0;
        let foulsAgainst = 0;
        let passesAgainst = 0;
        let sppAgainst = 0;

        const team = seasonData.divisions
            .flatMap(div => {
                if (!div || !Array.isArray(div.teams)) return [];
                return div.teams.map(t => ({
                    teamId: t?.teamId || '',
                    divisionName: div?.divisionName || '',
                    teamName: t?.teamName || 'Unknown',
                    coachId: t?.coachId || '',
                    race: t?.race || 'Unknown'
                }));
            })
            .find(t => t.teamId === teamId);

        if (!team) {
            log.warn('Team not found in divisions', { teamId });
            return null;
        }

        const coach = leagueCoaches.find(c => c && c.coachId === team.coachId);
        const coachName = coach ? coach.coachName : `Coach ${team.coachId || 'unknown'}`;

        seasonData.weeks.forEach((week, weekIndex) => {
            if (!week || !week.weekName) return;
            if (!isRegularSeasonWeekName(week.weekName)) return;
            if (weekIndex > upToWeekIndex) return;
            if (!Array.isArray(week.matches)) return;

            const match = week.matches.find(m => m && (m.homeTeamId === teamId || m.awayTeamId === teamId));
            if (match && match.results) {
                gamesPlayed++;

                const isHome = match.homeTeamId === teamId;
                const teamScore = isHome ? (match.results.homeTeamScore ?? 0) : (match.results.awayTeamScore ?? 0);
                const opponentScore = isHome ? (match.results.awayTeamScore ?? 0) : (match.results.homeTeamScore ?? 0);
                const teamCas = isHome ? (match.results.homeTeamCasInflicted ?? 0) : (match.results.awayTeamCasInflicted ?? 0);
                const opponentCas = isHome ? (match.results.awayTeamCasInflicted ?? 0) : (match.results.homeTeamCasInflicted ?? 0);

                touchdownsFor += teamScore;
                touchdownsAgainst += opponentScore;
                casualtiesFor += teamCas;
                casualtiesAgainst += opponentCas;

                kills += isHome ? (match.results.homeTeamKills ?? 0) : (match.results.awayTeamKills ?? 0);
                blocks += isHome ? (match.results.homeTeamBlocks ?? 0) : (match.results.awayTeamBlocks ?? 0);
                avBr += isHome ? (match.results.homeTeamAvBr ?? 0) : (match.results.awayTeamAvBr ?? 0);
                ko += isHome ? (match.results.homeTeamKO ?? 0) : (match.results.awayTeamKO ?? 0);
                surfs += isHome ? (match.results.homeTeamSurfs ?? 0) : (match.results.awayTeamSurfs ?? 0);
                fouls += isHome ? (match.results.homeTeamFouls ?? 0) : (match.results.awayTeamFouls ?? 0);
                passes += isHome ? (match.results.homeTeamPasses ?? 0) : (match.results.awayTeamPasses ?? 0);
                spp += isHome ? (match.results.homeTeamSPP ?? 0) : (match.results.awayTeamSPP ?? 0);
                killsAgainst += isHome ? (match.results.awayTeamKills ?? 0) : (match.results.homeTeamKills ?? 0);
                blocksAgainst += isHome ? (match.results.awayTeamBlocks ?? 0) : (match.results.homeTeamBlocks ?? 0);
                avBrAgainst += isHome ? (match.results.awayTeamAvBr ?? 0) : (match.results.homeTeamAvBr ?? 0);
                koAgainst += isHome ? (match.results.awayTeamKO ?? 0) : (match.results.homeTeamKO ?? 0);
                surfsAgainst += isHome ? (match.results.awayTeamSurfs ?? 0) : (match.results.homeTeamSurfs ?? 0);
                foulsAgainst += isHome ? (match.results.awayTeamFouls ?? 0) : (match.results.homeTeamFouls ?? 0);
                passesAgainst += isHome ? (match.results.awayTeamPasses ?? 0) : (match.results.homeTeamPasses ?? 0);
                sppAgainst += isHome ? (match.results.awayTeamSPP ?? 0) : (match.results.homeTeamSPP ?? 0);

                const opponentTeamId = isHome ? match.awayTeamId : match.homeTeamId;
                const opponent = seasonData.divisions
                    .flatMap(div => {
                        if (!div || !Array.isArray(div.teams)) return [];
                        return div.teams.map(t => ({
                            teamId: t?.teamId || '',
                            divisionName: div?.divisionName || ''
                        }));
                    })
                    .find(t => t.teamId === opponentTeamId);

                if (teamScore > opponentScore) {
                    wins++;
                    if (opponent && opponent.divisionName === team.divisionName) {
                        divisionWins++;
                    }
                } else if (teamScore < opponentScore) {
                    losses++;
                } else {
                    ties++;
                    if (opponent && opponent.divisionName === team.divisionName) {
                        divisionTies++;
                    }
                }
            }
        });

        const points = wins * 3 + ties;
        const divisionPoints = divisionWins * 3 + divisionTies;
        const diff = touchdownsFor - touchdownsAgainst;

        return {
            teamId,
            teamName: team.teamName || 'Unknown',
            race: team.race || 'Unknown',
            coachName,
            divisionName: team.divisionName || 'Unknown',
            gamesPlayed,
            wins,
            ties,
            losses,
            points,
            divisionPoints,
            touchdownsFor,
            touchdownsAgainst,
            diff,
            casualtiesFor,
            casualtiesAgainst,
            kills,
            blocks,
            avBr,
            ko,
            surfs,
            fouls,
            passes,
            spp,
            killsAgainst,
            blocksAgainst,
            avBrAgainst,
            koAgainst,
            surfsAgainst,
            foulsAgainst,
            passesAgainst,
            sppAgainst
        };
    } catch (error) {
        log.error('Error calculating team stats', { error: error instanceof Error ? error.message : String(error), teamId });
        return null;
    }
}

export function getStandingsThroughWeek(
    seasonData: SeasonStandingsInput,
    leagueCoaches: CoachModel[],
    upToWeekIndex: number
): Record<string, TeamStats[]> {
    try {
        const allTeamIds = seasonData.divisions.flatMap(div => {
            if (!div || !Array.isArray(div.teams)) return [];
            return div.teams.map(team => team?.teamId).filter((id): id is string => !!id);
        });
        const teamStats = allTeamIds
            .map(teamId => calculateTeamStats(seasonData, leagueCoaches, teamId, upToWeekIndex))
            .filter((stats): stats is TeamStats => stats !== null);

        const standingsByDivision = teamStats.reduce((acc, stats) => {
            if (!stats || !stats.divisionName) return acc;
            if (!acc[stats.divisionName]) {
                acc[stats.divisionName] = [];
            }
            acc[stats.divisionName].push(stats);
            return acc;
        }, {} as Record<string, TeamStats[]>);

        Object.keys(standingsByDivision).forEach(divisionName => {
            standingsByDivision[divisionName].sort(compareByTieBreakerWithinDivision);
        });

        return standingsByDivision;
    } catch (error) {
        log.error('Error calculating standings', { error: error instanceof Error ? error.message : String(error) });
        return {};
    }
}
